// Utilities
import { setDocumentTitle, useFetchWrapper, useHandleError, createSSEConnection } from '../helpers';
import { APIS } from '../constants';
import { GeoId } from '../types/geo.types';
import { currencies, dropdownList, geoJsonState, poiMapColorState, ptypeState, selectedGeoDropdownState } from '../states';
import { useRecoilValue, useSetRecoilState } from 'recoil';


const useMapsService = () => {
    const fetchWrapper = useFetchWrapper();
    const { getErrorMsg } = useHandleError()
    const setGeoDropdownList = useSetRecoilState(dropdownList);
    const setCurrencyList = useSetRecoilState(currencies);
    const setGeoJSON = useSetRecoilState(geoJsonState);
    const setOriginalGeoJSON = useSetRecoilState(geoJsonState);
    const setPoiMapColor = useSetRecoilState(poiMapColorState);
    const setPType = useSetRecoilState(ptypeState);
    const selectedGeoDropdown = useRecoilValue(selectedGeoDropdownState);

    const normalizeGeoId = (geoId: any): GeoId =>
        geoId == 1 || geoId === '1' ? 'EW-IN-IND-22-0' : geoId;

    const normalizePayload = (payload: any) => {
        if (!payload) return payload;
        const result = { ...payload };
        if (result.geoId != null) result.geoId = normalizeGeoId(result.geoId);
        if (result.geocode != null) result.geocode = normalizeGeoId(result.geocode);
        if (result.geoCode != null) result.geoCode = normalizeGeoId(result.geoCode);
        if (result.parentgeoId != null) result.parentgeoId = normalizeGeoId(result.parentgeoId);
        return result;
    };

    const getGeoDropdown = async (payload: any, geo_level?: string) => {
        // setSpinnerLite(prev => prev + 1);
        try {
            const response = await fetchWrapper.post(APIS.MAPS.GET_GEO_DROPDOWN_V5, normalizePayload(payload));
            if (geo_level) {
                setGeoDropdownList(prev => ({ ...prev, [geo_level]: response }));
            }
            // setSpinnerLite(prev => prev - 1);
            return response;
        } catch (error) {
            // setSpinnerLite(prev => prev - 1);
            throw error;
        }
    }
    const getGeoDropdownForPincode = async (payload: any, geo_level: string) => {
        // setSpinnerLite(prev => prev + 1);
        try {
            const response = await fetchWrapper.post(APIS.MAPS.GET_GEO_DROPDOWN_V5, normalizePayload(payload));
            setGeoDropdownList(prev => ({ ...prev, [geo_level]: response }));
            // setSpinnerLite(prev => prev - 1);
        } catch (error) {
            // setSpinnerLite(prev => prev - 1);
        }
    }

    const getGeoDropdownForVillage = async (payload: any, geo_level?: string) => {
        try {
            const response = await fetchWrapper.post(APIS.MAPS.GET_GEO_DROPDOWN_V5, normalizePayload(payload));
            if (geo_level) {
                setGeoDropdownList(prev => ({ ...prev, [geo_level]: response }));
            }
            return response;
        } catch (error) {
            getErrorMsg(error);
            throw error;
        }
    }

    // const getMap = (geoCode: number, currency: string) => {
    //     setSpinner(prev => ({ ...prev, count: prev.count + 1 }));
    //     return fetchWrapper.get(`${APIS.MAPS.GET_MAPS}?geoId=${geoCode}&currency=${currency}`)
    //         .then((data: any) => {
    //             setGeoJSON(data);
    //             setSpinner(prev => ({ ...prev, count: prev.count - 1 }));
    //         }).catch((error) => {
    //             setSpinner(prev => ({ ...prev, count: prev.count - 1 }));
    //         });;
    // }

    const getMap = (geoCode: GeoId, hex: boolean, catchments?: any, adjacency?: boolean, signal?: AbortSignal, onProgress?: (data: any) => void) => {
        geoCode = normalizeGeoId(geoCode);
        // setSpinner(prev => ({ ...prev, count: prev.count + 1 }));
        const url = `${APIS.MAPS.GET_MAP_BOUNDARY_V5}?hex=${hex}&geoId=${geoCode}${catchments ? `&${catchments}=true` : ''}${geoCode !== 'EW-IN-IND-22-0' && adjacency ? `&adjacency=true` : ""}`;

        // Initialize accumulator for SSE chunks (valid GeoJSON structure)
        let accumulatedData: any = {
            type: 'FeatureCollection',
            rootProperties: null,
            features: []
        };

        return new Promise((resolve, reject) => {
            const cleanup = createSSEConnection({
                url,
                signal,
                onMessage: (data: any) => {
                    try {
                        // Handle different chunk types
                        if (data.status === 'started') {
                            // Reset accumulator on start
                            accumulatedData = {
                                type: 'FeatureCollection',
                                rootProperties: null,
                                features: []
                            };
                            // Notify progress callback
                            if (onProgress) onProgress({ status: 'started', hex });
                        } else if (data.type === 'rootProperties') {
                            // Store root properties
                            accumulatedData.rootProperties = data.data;

                            // If no progress callback provided, use legacy behavior (backward compatibility)
                            if (!onProgress) {
                                const newGeoJSON = {
                                    type: 'FeatureCollection',
                                    rootProperties: { ...accumulatedData.rootProperties },
                                    features: []
                                };
                                setGeoJSON(newGeoJSON);
                                setOriginalGeoJSON(newGeoJSON);
                            }
                            setDocumentTitle(accumulatedData.rootProperties?.geoName || "");
                            // Notify progress callback
                            if (onProgress) onProgress({ type: 'rootProperties', hex, data: accumulatedData.rootProperties });
                        } else if (data.type === 'features' || data.type === 'hex' || data.type==="adjacency") {
                            // Append features to the array (handle both 'features' and 'hex' types)
                            if (Array.isArray(data.data)) {
                                accumulatedData.features = [...accumulatedData.features, ...data.data];

                                // If no progress callback provided, use legacy behavior (backward compatibility)
                                if (!onProgress) {
                                    const newGeoJSON = {
                                        type: 'FeatureCollection',
                                        rootProperties: accumulatedData.rootProperties ? { ...accumulatedData.rootProperties } : null,
                                        features: [...accumulatedData.features]
                                    };
                                    setGeoJSON(newGeoJSON);
                                    setOriginalGeoJSON(newGeoJSON);
                                }
                                // Notify progress callback with chunk info
                                if (onProgress) onProgress({
                                    type: data.type,
                                    hex,
                                    chunkSize: data.data.length,
                                    totalFeatures: accumulatedData.features.length,
                                    features: data.data // Pass the new features
                                });
                            }
                        }
                    } catch (error) {
                        console.error('Error processing SSE chunk:', error);
                    }
                },
                onComplete: () => {
                    // Final update when streaming is complete
                    if (accumulatedData.rootProperties) {
                        setPType(accumulatedData?.rootProperties?.type || null);
                        setDocumentTitle(accumulatedData.rootProperties?.geoName || "");
                    }
                    // If no progress callback provided, use legacy behavior (backward compatibility)
                    if (!onProgress) {
                        setGeoJSON({ ...accumulatedData });
                        setOriginalGeoJSON({ ...accumulatedData });
                    }
                    // Notify progress callback of completion
                    if (onProgress) onProgress({ status: 'complete', hex, totalFeatures: accumulatedData.features.length });
                    // setSpinner(prev => ({ ...prev, count: prev.count - 1 }));
                    resolve(accumulatedData);
                },
                onError: (error: any) => {
                    console.error('SSE connection error:', error);
                    // setSpinner(prev => ({ ...prev, count: prev.count - 1 }));
                    reject(error);
                }
            });

            // Cleanup on abort
            if (signal) {
                signal.addEventListener('abort', () => {
                    cleanup();
                    reject(new Error('Request aborted'));
                });
            }
        });
    }

    const getExploreNow = (searchTerm: string) => {
        return fetchWrapper.get(`${APIS.MAPS.GLOBAL_SEARCH}?key=${searchTerm}`);
        // return fetchWrapper.get(`${APIS.MAPS.GET_EXPLORE_NOW}?geo-code=1`);
    }

    const getFeaturedStories = (geoCode: GeoId, selCurrency: string) => {
        geoCode = normalizeGeoId(geoCode);
        return fetchWrapper.get(`${APIS.STORIES.GET_FEATURED_STORIES}?geo-code=${geoCode}&currency=${selCurrency}`)
    }

    const getCoreSolutions = (geoCode: GeoId) => {
        geoCode = normalizeGeoId(geoCode);
        return fetchWrapper.get(`${APIS.MAPS.GET_CORE_SOLUTIONS}?geo-code=${geoCode}`);
    }

    const getMyPresence = (geoCode: GeoId) => {
        geoCode = normalizeGeoId(geoCode);
        return fetchWrapper.get(`${APIS.MAPS.GET_MY_PRESENCE}?geoId=${geoCode}`);
    }

    const getCurrencies = () => {
        // setSpinner(prev => ({ ...prev, count: prev.count + 1 }));
        return fetchWrapper.get(APIS.MAPS.GET_CURRENCIES)
            .then(response => {
                setCurrencyList(response);
                // setSpinner(prev => ({ ...prev, count: prev.count - 1 }));
            })
            .catch(error => {
                getErrorMsg(error);
                // setSpinner(prev => ({ ...prev, count: prev.count - 1 }));
            });
    }

    const getMapPoiColor = (payload: any, signal?: AbortSignal) => {

        console.log("COLOR PAYLOAD --------->" , payload)
        // setSpinnerLite(prev => prev + 1);
        return fetchWrapper.post(APIS.MAPS.GET_POI_MAP_COLOR_V3, payload, signal)
            .then(response => {
                // Only update state if request wasn't aborted
                if (!signal?.aborted) {
                    setPoiMapColor(response);
                }
                return response;
            })
            .catch(error => {
                // Don't show error for aborted requests
                if (error?.name !== 'AbortError' && error?.name !== 'CanceledError' && !signal?.aborted) {
                    getErrorMsg(error);
                }
                throw error;
            }).finally(() => {
                // setSpinnerLite(prev => prev - 1);
            });
    }

    const getTopDistricts = (payload: any) => {
        // setSpinnerLite(prev => prev + 1)
        return fetchWrapper.post(APIS.MAPS.GET_TOP_DISTRICTS, payload);
    }

    const getBrands = (payload: any) => {
        return fetchWrapper.post(APIS.MAPS.GET_BRANDS, payload);
    }

    const getMapProperties = (payload: any) => {
        return fetchWrapper.post(APIS.MAPS.GET_MAP_PROPERTIES_V5, normalizePayload(payload));
    }

    const getRecommendedRegions = (payload: any, signal?: AbortSignal) => {
        if(selectedGeoDropdown?.geotype === "catchment") 
            {
                return
            }
        return fetchWrapper.post(APIS.MAPS.GET_TOP_REGIONS_EXTENDED_V2, payload, signal);
    }

    const getTopPois = (payload: any, signal?: AbortSignal) => {
        return fetchWrapper.post(APIS.MAPS.GET_TOP_POIS, normalizePayload(payload), signal);
    }

    const getCatchmentTypes = (geoId: GeoId) => {
        geoId = normalizeGeoId(geoId);
        return fetchWrapper.get(`${APIS.MAPS.GET_CATCHMENT_TYPES}?geoId=${geoId}`);
    }

    return {
        getGeoDropdown,
        getGeoDropdownForPincode,
        getGeoDropdownForVillage,
        getMap,
        getExploreNow,
        getFeaturedStories,
        getCoreSolutions,
        getMyPresence,
        getCurrencies,
        getMapPoiColor,
        getTopDistricts,
        getBrands,
        getMapProperties,
        getRecommendedRegions,
        getTopPois,
        getCatchmentTypes,
    }
}

export { useMapsService };